import type { Seat } from './media';

// ---------------------------------------------------------------------------
// Where everybody sits on the stage (G2), as pure arithmetic.
//
// The stage page draws the room in the box layout the programme uses —
// programme._panel_boxes on the render plane, the host first and largest —
// so a live show and a rendered one read as the same show. 099 seated five;
// 207 seats eight (Tom: "up to 8 cameras when calling up LIVE visitors to
// the panel"). The numbers live here, away from the component, so a test can
// hold them against the render plane's without a browser.
// ---------------------------------------------------------------------------

export const W = 1920, H = 1080;

/** The most seats the stage draws: a host and seven guests. The same number as programme.CAST_CAP. */
export const CAST_CAP = 8;

/** The programme's box geometry (programme._panel_boxes), one to eight. */
export function boxes(n: number): Array<[number, number, number, number]> {
  const m = 48, top = 120, bottom = 620 + 260;   // the stage has no caption band: the boxes take the height
  n = Math.min(Math.max(1, Math.floor(n)), CAST_CAP);
  if (n <= 1) return [[m, top, W - m, bottom]];
  if (n === 2) { const half = Math.floor((W - m * 3) / 2); return [[m, top, m + half, bottom], [m * 2 + half, top, W - m, bottom]]; }
  if (n === 3) {
    const lead = Math.floor((W - m * 3) * 0.55), sh = Math.floor((bottom - top - m / 2) / 2), x2 = m * 2 + lead;
    return [[m, top, m + lead, bottom], [x2, top, W - m, top + sh], [x2, top + sh + m / 2, W - m, bottom]];
  }
  if (n === 4) {
    const cw = Math.floor((W - m * 3) / 2), ch = Math.floor((bottom - top - m / 2) / 2);
    return [[m, top, m + cw, top + ch], [m * 2 + cw, top, W - m, top + ch], [m, top + ch + m / 2, m + cw, bottom], [m * 2 + cw, top + ch + m / 2, W - m, bottom]];
  }
  if (n === 5) {
    const lead = Math.floor((W - m * 3) * 0.44), x2 = m * 2 + lead, gw = Math.floor((W - m - x2 - m / 2) / 2), gh = Math.floor((bottom - top - m / 2) / 2);
    return [[m, top, m + lead, bottom], [x2, top, x2 + gw, top + gh], [x2 + gw + m / 2, top, W - m, top + gh], [x2, top + gh + m / 2, x2 + gw, bottom], [x2 + gw + m / 2, top + gh + m / 2, W - m, bottom]];
  }
  // Six to eight (207): the host keeps the lead on the left; the guests sit
  // in two rows beside it, three across for five or six of them, four for seven.
  const lead = Math.floor((W - m * 3) * 0.36), x2 = m * 2 + lead, gap = m / 2;
  const cols = Math.ceil((n - 1) / 2);
  const gw = Math.floor((W - m - x2 - gap * (cols - 1)) / cols), gh = Math.floor((bottom - top - gap) / 2);
  const out: Array<[number, number, number, number]> = [[m, top, m + lead, bottom]];
  for (let g = 0; g < n - 1; g++) {
    const r = Math.floor(g / cols), c = g % cols;
    const x0 = x2 + c * (gw + gap), y0 = top + r * (gh + gap);
    out.push([x0, y0, c === cols - 1 ? W - m : x0 + gw, r === 1 ? bottom : y0 + gh]);
  }
  return out;
}

/** SN2: with a screen shared, it takes the left; the faces stack in a column on the right. */
export function screenLayout(faces: number): { screen: [number, number, number, number]; faces: Array<[number, number, number, number]> } {
  const m = 48, top = 120, bottom = 880;
  const sw = Math.floor((W - m * 3) * 0.72), x2 = m * 2 + sw;
  const n = Math.max(1, Math.min(4, faces));
  const gap = 24, fh = Math.floor((bottom - top - gap * (n - 1)) / n);
  const col = Array.from({ length: n }, (_, i): [number, number, number, number] => [x2, top + i * (fh + gap), W - m, top + i * (fh + gap) + fh]);
  return { screen: [m, top, m + sw, bottom], faces: col };
}

/** The host first, then guests in the order they arrived; eight at most. */
export function order(seats: Seat[]): Seat[] {
  const host = seats.filter((s) => s.identity.startsWith('host-'));
  const rest = seats.filter((s) => !s.identity.startsWith('host-'));
  return [...host, ...rest].slice(0, CAST_CAP);
}
